/**
 * "What leather object is under the cursor?" — shared by the leather Mirror
 * (#973) and Round (#974) methods, both of which let a press pick the object to
 * work on next.
 *
 * `Scene.hitTest` alone is not enough, and the reason is easy to miss: in the
 * leather display mode a cutout is a VOID. Nothing is painted inside one, so a
 * hit test in the hole you can plainly see resolves to no item at all and the
 * click reads as "you clicked nothing". The geometric containment fallback here
 * covers exactly that case.
 *
 * Kept out of both controllers because it's the same rule with the same trap;
 * a second copy is what #990 records the cost of.
 */
import type paper from "paper";
import type { Scene } from "../canvas/Scene";
import { isVisible, pieceStitchLines, type Project } from "../model/Project";
import { pieceDisplayOffset } from "../model/pieceTransform";
import { seamPathData } from "../model/seamGeometry";
import { currentPointerKind, hitRadiusFor } from "../canvas/pointerPrecision";

/** How near (screen px) the cursor must come to a seam's PATH for it to count
 *  as picked. A seam renders as a row of chisel marks with nothing drawn
 *  between them, so hit-testing the painted items alone means only the marks
 *  themselves are targetable — the line the user sees implied is not. This is
 *  the grace distance that makes a seam as easy to point at as it looks. */
const SEAM_PICK_TOL_PX = 8;

export type LeatherPick =
  | { kind: "stitch"; pieceId: string; stitchId: string }
  | { kind: "cutout"; pieceId: string; cutoutId: string }
  | { kind: "piece"; pieceId: string }
  | null;

/**
 * The topmost visible cutout whose outline contains `p`, or null. Walks
 * back-to-front so the most recently added cutout wins an overlap, matching the
 * z-order `hitTest` reports for painted items.
 */
export function cutoutContaining(
  project: Project,
  partLayoutView: "spread" | "assembled",
  scope: paper.PaperScope,
  p: { x: number; y: number },
): { pieceId: string; cutoutId: string } | null {
  const pt = new scope.Point(p.x, p.y);
  for (let i = project.pieces.length - 1; i >= 0; i--) {
    const piece = project.pieces[i]!;
    if (!isVisible(piece)) continue;
    const off = pieceDisplayOffset(piece, partLayoutView);
    const cutouts = piece.cutouts ?? [];
    for (let c = cutouts.length - 1; c >= 0; c--) {
      const cut = cutouts[c]!;
      if (!isVisible(cut)) continue;
      let cp: paper.CompoundPath | null = null;
      try {
        cp = new scope.CompoundPath({ pathData: cut.pathData, insert: false });
      } catch {
        continue;
      }
      // Cutout geometry is stored in the part's ORIGINAL space; the cursor is
      // in DISPLAY space, so shift the probe rather than the path.
      const hit = cp.contains(pt.subtract(new scope.Point(off.dx, off.dy)));
      cp.remove();
      if (hit) return { pieceId: piece.id, cutoutId: cut.id };
    }
  }
  return null;
}

/**
 * Resolve a press position to the leather object it picks: a seam, a cutout, or
 * the owning part. Returns null over bare mat.
 *
 * `includeStitch: false` folds a seam hit into its part — for a tool that has
 * nothing to do with seams (Round: a seam has no corners of its own), selecting
 * the part the user is pointing at beats selecting nothing.
 */
export function pickLeatherObjectAt(
  scene: Scene,
  project: Project,
  scope: paper.PaperScope,
  p: { x: number; y: number },
  opts: { includeStitch: boolean },
): LeatherPick {
  const hit = scene.hitTest(new scope.Point(p.x, p.y));
  if (opts.includeStitch && hit.stitchId && hit.pieceId) {
    return { kind: "stitch", pieceId: hit.pieceId, stitchId: hit.stitchId };
  }
  // `hitTest` only sees the chisel MARKS, so without this a seam is targetable
  // only on its individual holes and not along the run between them — which is
  // the thing the user is actually aiming at. Fall back to distance from the
  // seam's path, and let a near seam win over the part underneath it: the seam
  // sits on top, so that's what the cursor is pointing at.
  if (opts.includeStitch) {
    const seam = seamNear(project, scene.getPartLayoutView(), scope, p);
    if (seam) return { kind: "stitch", ...seam };
  }
  if (hit.cutoutId && hit.pieceId) {
    return { kind: "cutout", pieceId: hit.pieceId, cutoutId: hit.cutoutId };
  }
  if (!hit.cutoutId) {
    const inside = cutoutContaining(
      project,
      scene.getPartLayoutView(),
      scope,
      p,
    );
    if (inside) return { kind: "cutout", ...inside };
  }
  if (hit.pieceId) return { kind: "piece", pieceId: hit.pieceId };
  return null;
}

/**
 * The seam whose path passes nearest `p`, within the pick tolerance, or null.
 *
 * Distance is measured to the seam's PATH — including its closing segment when
 * the seam is closed, which is otherwise a gap you can't point at on an offset
 * seam that rings a part.
 */
export function seamNear(
  project: Project,
  partLayoutView: "spread" | "assembled",
  scope: paper.PaperScope,
  p: { x: number; y: number },
): { pieceId: string; stitchId: string } | null {
  const zoom = scope.view?.zoom && scope.view.zoom > 0 ? scope.view.zoom : 1;
  const tolMm = hitRadiusFor(SEAM_PICK_TOL_PX, currentPointerKind()) / zoom;
  const pt = new scope.Point(p.x, p.y);
  let best: { pieceId: string; stitchId: string; dist: number } | null = null;
  for (const piece of project.pieces) {
    if (!isVisible(piece)) continue;
    const off = pieceDisplayOffset(piece, partLayoutView);
    // Seam points are stored in the part's ORIGINAL space; the cursor is in
    // DISPLAY space, so shift the probe rather than every point.
    const probe = pt.subtract(new scope.Point(off.dx, off.dy));
    for (const seam of pieceStitchLines(piece)) {
      if (isVisible(seam) === false) continue;
      if (seam.points.length < 2) continue;
      // Real geometry, curves included (#982): a curved seam picked by the
      // chords between its anchors is hardest to hit exactly where it bulges
      // furthest from them.
      const path = new scope.Path({
        pathData: seamPathData(seam),
        insert: false,
      });
      path.closed = seam.closed === true;
      const near = path.getNearestPoint(probe);
      const dist = near ? near.getDistance(probe) : Infinity;
      path.remove();
      if (dist > tolMm) continue;
      if (!best || dist < best.dist) {
        best = { pieceId: piece.id, stitchId: seam.id, dist };
      }
    }
  }
  return best ? { pieceId: best.pieceId, stitchId: best.stitchId } : null;
}

/** The `SceneSelection` patch a pick corresponds to. Every sibling id is nulled
 *  so the scopes stay mutually exclusive. */
export function selectionForPick(
  pick: NonNullable<LeatherPick>,
): Parameters<Scene["setSelection"]>[0] {
  if (pick.kind === "stitch") {
    return {
      pieceId: pick.pieceId,
      decorationId: null,
      holeId: null,
      cutoutId: null,
      stitchId: pick.stitchId,
      stitchIds: [pick.stitchId],
    };
  }
  if (pick.kind === "cutout") {
    return {
      pieceId: pick.pieceId,
      decorationId: null,
      holeId: null,
      stitchId: null,
      cutoutId: pick.cutoutId,
    };
  }
  return {
    pieceId: pick.pieceId,
    pieceIds: [pick.pieceId],
    decorationId: null,
    holeId: null,
    cutoutId: null,
    stitchId: null,
  };
}
